import React, {Suspense} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import {useNavigate} from 'react-router';
import clsx from 'clsx';
import {diApiEu} from '@di-shared/api/di';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import {ROUTE_PATHS} from '@di-shared/routes';
import {getQueryKey} from '@shared/queryKeyMap';
import {useSuspenseQuery} from '@tanstack/react-query';

interface HomeTireEuProps {
  vin: string;
}

enum TireStatus {
  NOT_REGISTERED,
  WARNING,
  GOOD,
}

function HomeTireEu({vin}: HomeTireEuProps) {
  return (
    <ErrorBoundary fallback={<HomeTireEuError vin={vin} />}>
      <Suspense fallback={<HomeTireEuLoading />}>
        <HomeTireEuInner vin={vin} />
      </Suspense>
    </ErrorBoundary>
  );
}

// ============================================================================
// Inner Component with Business Logic
// ============================================================================

function HomeTireEuInner({vin}: HomeTireEuProps) {
  const {t} = useInferableTranslation();
  const navigate = useNavigate();
  const {data} = useSuspenseQuery({
    queryKey: [getQueryKey(diApiEu.tire.getTireBound), vin],
    queryFn: () => diApiEu.tire.getTireBound({vin}),
  });
  const tireBoundData = data.data;

  // Calculate overall tire status
  function calculateTireStatus(): TireStatus {
    /**
     * [2026-03-10:Jeyoung Lee]
     * @link https://www.figma.com/design/wC32nUknKZgWHMd2EynePs/branch/OLbLJl0GGkdR0SCoLxiOLp/01.-DI-Home?node-id=17194-4654&t=mlDWSxne1K5JzLc1-11
     * Good
     * • 타이어공기압이 모두 정상 or 높음일 경우
     */

    const {flBound, frBound, rlBound, rrBound} = tireBoundData;
    const hasWarning = ![flBound, frBound, rlBound, rrBound].every((bound) => bound === 'inbound' || bound === 'higher');
    const isTireRegistered = [flBound, frBound, rlBound, rrBound].every((bound) => !!bound);
    if (!isTireRegistered) {
      return TireStatus.NOT_REGISTERED;
    }
    return hasWarning ? TireStatus.WARNING : TireStatus.GOOD;
  }

  const tireStatus = calculateTireStatus();

  return (
    <section className="section section_car_diagnosis">
      <ul className="car_diagnosis_list">
        <li className="car_diagnosis_item">
          <a
            className="car_diagnosis_link"
            onClick={() => navigate(ROUTE_PATHS.DIAG_TIRE.getFullPath(vin))}
          >
            <div className="section_container">
              <strong className="car_diagnosis_title">{t('ctn_tire_title_eu')}</strong>
              {tireStatus === TireStatus.GOOD ? (
                <div className="badge_box">
                  <span className="badge badge_basic_container badge_20px">{t('ctn_tire_status_good')}</span>
                </div>
              ) : tireStatus === TireStatus.WARNING ? (
                <div className="badge_box">
                  <span className="badge badge_container_red badge_20px">{t('ctn_tire_status_Needservice')}</span>
                </div>
              ) : (
                <></>
              )}
              {/**
               * [2026-03-10:Jeyoung Lee]
               * @link https://www.figma.com/design/wC32nUknKZgWHMd2EynePs/branch/OLbLJl0GGkdR0SCoLxiOLp/01.-DI-Home?node-id=17194-4654&t=mlDWSxne1K5JzLc1-11
               * 문구 미표시
               * • 차량에 설정된 타이어 스펙이 없을 경우
               * • api error 시
               * No text displayed일떄 타이어 아이콘 구분 없는 것 --> 'good' 상태 아이콘 활용
               */}
              <span
                className={clsx(
                  'ic',
                  tireStatus === TireStatus.GOOD || tireStatus === TireStatus.NOT_REGISTERED ? 'ic_tire_good' : 'ic_tire_warning'
                )}
              ></span>
            </div>
          </a>
        </li>
      </ul>
    </section>
  );
}

function HomeTireEuLoading() {
  return (
    <section className="section section_car_diagnosis">
      <ul className="car_diagnosis_list">
        <li className="car_diagnosis_item">
          <div
            className="car_diagnosis_link skeleton"
            style={{height: 90}}
          />
        </li>
      </ul>
    </section>
  );
}

function HomeTireEuError({vin}: {vin: string}) {
  const {t} = useInferableTranslation();
  const navigate = useNavigate();
  return (
    <section className="section section_car_diagnosis">
      <ul className="car_diagnosis_list">
        <li className="car_diagnosis_item">
          <a
            className="car_diagnosis_link"
            onClick={() => navigate(ROUTE_PATHS.DIAG_TIRE.getFullPath(vin))}
          >
            <div className="section_container">
              <strong className="car_diagnosis_title">{t('ctn_tire_title_eu')}</strong>
              <span className="ic ic_tire_good"></span>
            </div>
          </a>
        </li>
      </ul>
    </section>
  );
}
export default HomeTireEu;
